import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import './Modal.css';

// Global stack to track modal order (most recent last)
let modalStack = [];

const Modal = ({
    children,
    title,
    onClose,
    size = 'medium',
    showCloseButton = true,
    closeOnOverlayClick = true,
    headerActions = null,
    className = '',
    zIndex = null
}) => {
    const modalId = useRef(Date.now() + Math.random());

    // Register this modal in the stack
    useEffect(() => {
        modalStack.push(modalId.current);
        return () => {
            modalStack = modalStack.filter(id => id !== modalId.current);
        };
    }, []);

    // Cerrar modal con tecla ESC
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                // No cerrar si el evento ya fue manejado por otro componente
                if (e.defaultPrevented) {
                    return;
                }
                // Only close if this is the topmost modal
                const isTopmost = modalStack[modalStack.length - 1] === modalId.current;
                if (isTopmost) {
                    e.stopPropagation();
                    e.preventDefault();
                    onClose();
                }
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    // Prevenir scroll del body cuando el modal está abierto
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const handleOverlayClick = (e) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) {
            onClose();
        }
    };

    const sizeClass = {
        small: 'modal-small',
        medium: 'modal-medium',
        large: 'modal-large',
        xlarge: 'modal-xlarge',
        fullscreen: 'modal-fullscreen'
    }[size] || 'modal-medium';

    // Estilos especiales para modal de facturación (sin altura fija)
    const isAutoHeight = className.includes('modal-facturacion-container');
    const containerStyle = isAutoHeight ? { height: 'auto' } : {};
    const contentStyle = isAutoHeight ? { flex: 'none' } : {};

    const overlayStyle = zIndex ? { zIndex } : {};

    return (
        <div className="modal-overlay" onClick={handleOverlayClick} style={overlayStyle}>
            <div className={`modal-container ${sizeClass} ${className}`} style={containerStyle}>
                {(title || showCloseButton) && (
                    <div className="modal-header">
                        <div className="modal-header-left">
                            {title && (
                                <h2 className="modal-title">{title}</h2>
                            )}
                            {headerActions && (
                                <div className="modal-header-actions">
                                    {headerActions}
                                </div>
                            )}
                        </div>
                        {showCloseButton && (
                            <button
                                className="modal-close-button"
                                onClick={onClose}
                                type="button"
                                aria-label="Cerrar modal"
                            >
                                <X size={20} />
                            </button>
                        )}
                    </div>
                )}

                <div className="modal-content" style={contentStyle}>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default Modal;